import { Injectable, inject, OnDestroy, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { AuthService } from './auth.service';
import { Store } from '@ngrx/store';
import { AppState } from '../../store/app.state';
import * as AuthActions from '../../store/auth/auth.actions';
import { Router } from '@angular/router';
import { SwalService } from './swal.service';
import { UrlService } from './url.service';

@Injectable({
  providedIn: 'root',
})
export class TokenRefreshService implements OnDestroy {
  private authService = inject(AuthService);
  private store = inject(Store<AppState>);
  private router = inject(Router);
  private swalService = inject(SwalService);
  private platformId = inject(PLATFORM_ID);

  private refreshInterval: any;
  private readonly REFRESH_INTERVAL = 5 * 60 * 1000; // 5 minutes

  constructor() {
    // Ne démarrer le refresh que côté client
    if (isPlatformBrowser(this.platformId)) {
      this.startTokenRefresh();
    }
  }

  ngOnDestroy(): void {
    this.stopTokenRefresh();
  }

  /**
   * Démarre la vérification périodique des tokens
   */
  startTokenRefresh(): void {
    // Ne fonctionner que côté client
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    // Vérifier immédiatement
    this.checkTokenStatus();

    // Puis vérifier toutes les 5 minutes
    this.refreshInterval = setInterval(() => {
      this.checkTokenStatus();
    }, this.REFRESH_INTERVAL);
  }

  /**
   * Arrête la vérification périodique des tokens
   */
  stopTokenRefresh(): void {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
      this.refreshInterval = null;
    }
  }

  /**
   * Vérifie le statut du token et le rafraîchit si nécessaire
   */
  private async checkTokenStatus(): Promise<void> {
    // Ne fonctionner que côté client
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    if (!this.authService.isAuthenticated()) {
      return;
    }

    try {
      const isTokenValid = await this.authService.checkAndRefreshToken();

      if (!isTokenValid) {
        console.log('🔐 Token invalide, déconnexion automatique');
        this.handleTokenExpiration();
      }
    } catch (error) {
      console.error('🔐 Erreur lors de la vérification du token:', error);
      this.handleTokenExpiration();
    }
  }

  /**
   * Gère l'expiration du token en déconnectant l'utilisateur
   */
  private handleTokenExpiration(): void {
    this.authService.logout();
    this.store.dispatch(AuthActions.logout());

    this.swalService.warning(
      'Session expirée',
      'Votre session a expiré. Vous avez été déconnecté.'
    );

    const urlService = new UrlService();
    this.router.navigate(urlService.buildRouteSegments(''));
  }

  /**
   * Force la vérification du token (utile pour les actions critiques)
   */
  async forceTokenCheck(): Promise<boolean> {
    if (!this.authService.isAuthenticated()) {
      return false;
    }

    try {
      return await this.authService.checkAndRefreshToken();
    } catch (error) {
      console.error(
        '🔐 Erreur lors de la vérification forcée du token:',
        error
      );
      this.handleTokenExpiration();
      return false;
    }
  }
}
